import classNames from 'classnames';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { TextFieldTheme, TextField as TPATextField } from '@wix/wix-ui-tpa/cssVars';
import { useFieldAttributes } from '../../services/field-attributes';
import { FieldDescriptionWUT } from '../field-description/field-description-wut';
import { FieldErrorWUT } from '../field-error/field-error-wut';
import { FieldLabelWUT } from '../field-label/field-label-wut';
import { classes } from './payment-input-field-wut.st.css';
import { useConfig } from '../../contexts/config-context';
import { toProductValue } from './payment-input-field-utils';
import { useInvokeCallback } from '../shared/use-invoke-callback';
import { ForcedState, useForcedState } from '../../contexts/forced-state-context';
import { formatPrice, getCurrencyAriaLabel } from '../shared/payment-field-utils';
import { useTranslation } from '../../contexts/translation-context';
import { useFocusFieldEvent } from '../../contexts/events-context';
import { Widget } from '../widget/widget';
export const PaymentInputFieldWUT = _ref => {
  var _value$at;
  let {
    id,
    target,
    label,
    hideLabel = false,
    placeholder,
    required = false,
    value = null,
    onChange,
    onFocus,
    onBlur,
    error,
    hasError = false,
    errorMessage,
    disabled = false,
    readOnly,
    description,
    validation,
    FieldLayout,
    requiredIndicatorType,
    requiredIndicatorPlacement,
    widgetHtml,
    widgetEnabled
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    labelId,
    inputId,
    errorId,
    descriptionId,
    ariaDescribedBy,
    prefixId
  } = useFieldAttributes(id, error, description);
  const {
    currency,
    regionalFormat
  } = useConfig();
  const forcedHover = useForcedState().includes(ForcedState.INPUT_FIELDS_HOVER);
  const invokeBlur = useInvokeCallback(onBlur);
  const inputRef = useRef(null);
  const paymentValidation = useMemo(() => {
    var _validation$predefine;
    return validation == null || (_validation$predefine = validation.predefined) == null || (_validation$predefine = _validation$predefine.paymentOptions) == null || (_validation$predefine = _validation$predefine.products) == null ? void 0 : _validation$predefine[0];
  }, [validation]);
  const [paymentValue, setPaymentValue] = useState(String((value == null || (_value$at = value.at(0)) == null ? void 0 : _value$at.price) ?? ''));
  const handleOnBlur = useCallback(event => {
    const price = formatPrice(event.target.value, regionalFormat);
    setPaymentValue(price ? price : '');
    invokeBlur();
  }, [regionalFormat, invokeBlur]);
  const handleOnChange = useCallback(event => {
    setPaymentValue(event.target.value);
    const filteredInput = formatPrice(event.target.value, regionalFormat);
    if (filteredInput) {
      const normalizedValue = Number(filteredInput.replace(/,/g, '.'));
      onChange([toProductValue(paymentValidation, normalizedValue)]);
    } else {
      onChange(null);
    }
  }, [setPaymentValue, onChange, paymentValidation, regionalFormat]);
  useFocusFieldEvent(() => {
    var _inputRef$current;
    (_inputRef$current = inputRef.current) == null || _inputRef$current.focus({
      preventScroll: true
    });
  }, id);
  return /*#__PURE__*/React.createElement(FieldLayout, {
    fieldId: id,
    renderLabel: () => /*#__PURE__*/React.createElement(FieldLabelWUT, {
      labelId: labelId,
      required: required,
      label: label,
      hideLabel: hideLabel,
      requiredIndicatorType: requiredIndicatorType,
      requiredIndicatorPlacement: requiredIndicatorPlacement
    }),
    renderInput: () => widgetHtml && widgetEnabled ? /*#__PURE__*/React.createElement(Widget, {
      fieldId: id,
      widgetHtml: widgetHtml,
      onChange: onChange,
      value: value
    }) : /*#__PURE__*/React.createElement(TPATextField, {
      id: inputId,
      placeholder: placeholder,
      "aria-label": label,
      "aria-labelledby": `${labelId} ${prefixId}`,
      "aria-describedby": ariaDescribedBy,
      className: classNames(classes.paymentInput, classes.theme, {
        [classes.hover]: forcedHover
      }),
      value: value ? paymentValue : '',
      name: target,
      onChange: handleOnChange,
      disabled: disabled,
      readOnly: readOnly,
      onBlur: handleOnBlur,
      onFocus: onFocus,
      theme: TextFieldTheme.Box,
      error: hasError,
      newErrorMessage: true,
      prefix: currency ? /*#__PURE__*/React.createElement("span", {
        id: prefixId,
        "aria-hidden": false,
        "aria-label": getCurrencyAriaLabel(t, currency)
      }, currency.sign || currency.code) : null,
      autoComplete: "off",
      required: required,
      ref: inputRef
    }),
    renderDescription: () => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FieldErrorWUT, {
      target: target,
      hasError: hasError,
      errorMessage: errorMessage,
      id: errorId
    }), /*#__PURE__*/React.createElement(FieldDescriptionWUT, {
      description: description,
      id: descriptionId
    }))
  });
};
//# sourceMappingURL=payment-input-field-wut.js.map